import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingDown, Briefcase, CloudRain, Frown, TrendingUp, Globe, Sun, Smile, Sparkles, ArrowRight } from "lucide-react";

const PROBLEMS = [
  { title: "Low Salary",     desc: "₹5–8 LPA after years of grinding. Hardly any room to grow.",  icon: TrendingDown },
  { title: "No Core Jobs",    desc: "Your degree is gold but the market doesn't have openings.",   icon: Briefcase },
  { title: "Poor Climate",    desc: "47°C summers, smog winters, monsoon floods. No chill.",       icon: CloudRain },
  { title: "Lifestyle Stuck", desc: "Same routine. Same friends. Zero growth in your story.",      icon: Frown },
];

const SOLUTIONS = [
  { title: "Top Salaries",      desc: "$80K–$150K starting packages. Earn in dollars, pounds, euros.", icon: TrendingUp },
  { title: "Top Global Jobs",   desc: "Google, Meta, Apple, Microsoft, JP Morgan — all hiring you.",   icon: Globe },
  { title: "Awesome Climate",   desc: "Four real seasons. Clean air. Hike, ski, beach — repeat.",     icon: Sun },
  { title: "Glow-Up Lifestyle", desc: "Independence, world travel, global friends, top-tier culture.", icon: Smile },
];

// Confetti / sparkle burst
function Burst({ trigger }) {
  const [parts, setParts] = useState([]);
  useEffect(() => {
    if (trigger == null) return;
    const items = Array.from({ length: 14 }, (_, i) => ({
      id: `${trigger}-${i}`,
      angle: (i / 14) * Math.PI * 2,
      dist: 70 + Math.random() * 60,
      delay: Math.random() * 0.05,
      size: 6 + Math.random() * 6,
      color: i % 3 === 0 ? "#FAFAFA" : "#c92e34",
    }));
    setParts(items);
    const t = setTimeout(() => setParts([]), 900);
    return () => clearTimeout(t);
  }, [trigger]);

  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {parts.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: 0, y: 0, opacity: 1, scale: 0.4 }}
          animate={{
            x: Math.cos(p.angle) * p.dist,
            y: Math.sin(p.angle) * p.dist,
            opacity: 0,
            scale: 1.1,
            rotate: Math.random() * 240,
          }}
          transition={{ duration: 0.85, delay: p.delay, ease: "easeOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: p.size, height: p.size, background: p.color, boxShadow: `0 0 8px ${p.color}` }}
        />
      ))}
    </div>
  );
}

function Card({ item, side, index }) {
  const Icon = item.icon;
  const isLeft = side === "left";
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: isLeft ? -6 : 6, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, rotate: isLeft ? -1.5 : 1.5, scale: 1 }}
      exit={{ opacity: 0, y: -40, rotate: isLeft ? 6 : -6, scale: 0.85, transition: { duration: 0.25 } }}
      transition={{ delay: index * 0.07, type: "spring", stiffness: 110, damping: 14 }}
      whileHover={{ y: -6, rotate: isLeft ? -3 : 3, scale: 1.03 }}
      style={{ transformPerspective: 1000 }}
      className={`relative overflow-hidden rounded-2xl border-2 p-3 md:p-6 ${
        isLeft
          ? "bg-white text-[#282829] border-[#282829] brutal-shadow-red"
          : "bg-[#c92e34] text-white border-white brutal-shadow-ink"
      }`}
      data-testid={`toggle-card-${side}-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Glossy highlight */}
      <div className={`absolute inset-x-3 top-1 h-7 rounded-full ${isLeft ? "bg-[#c92e34]/8" : "bg-white/15"} blur-sm pointer-events-none`} />

      <div className="relative flex items-start gap-2.5 md:gap-4">
        <div
          className={`w-9 h-9 md:w-14 md:h-14 rounded-xl md:rounded-2xl grid place-items-center flex-shrink-0 border-2 ${
            isLeft
              ? "bg-[#c92e34] text-white border-[#282829]"
              : "bg-white text-[#c92e34] border-white"
          }`}
        >
          <Icon size={16} strokeWidth={2.5} className="md:w-[22px] md:h-[22px]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-xl md:text-3xl leading-tight md:leading-none tracking-tight pr-14 md:pr-16">
            {item.title}
          </div>
          <p className={`text-xs md:text-sm mt-1.5 md:mt-2 leading-snug ${isLeft ? "text-[#282829]/80" : "text-white/95"}`}>
            {item.desc}
          </p>
        </div>
      </div>
      {/* corner badge — sits inside the card edge so overflow-hidden doesn't clip it */}
      <div
        className={`absolute top-2 md:top-3 ${isLeft ? "right-2 md:right-3" : "left-2 md:left-3"} font-anton text-[9px] md:text-[10px] tracking-[0.18em] uppercase px-1.5 md:px-2 py-0.5 rounded-full border-2 ${
          isLeft
            ? "bg-[#282829] text-white border-white -rotate-6"
            : "bg-white text-[#c92e34] border-[#282829] rotate-6"
        }`}
      >
        {isLeft ? "Pain" : "Gain"}
      </div>
    </motion.div>
  );
}

export default function ProblemSolutionToggle() {
  const [on, setOn] = useState(false);
  const [burstTick, setBurstTick] = useState(null);
  const knobRef = useRef(null);

  const flip = () => {
    setOn((v) => !v);
    setBurstTick(Date.now());
  };

  const cards = on ? SOLUTIONS : PROBLEMS;

  return (
    <section
      className="relative bg-[#282829] text-white py-12 md:py-28 overflow-hidden border-y-[3px] border-[#c92e34]"
      data-testid="problem-solution-toggle"
    >
      {/* Animated background — colour shifts on toggle */}
      <motion.div
        animate={{ opacity: on ? 0.18 : 0.08 }}
        transition={{ duration: 0.6 }}
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      <motion.div
        animate={{ opacity: on ? 0.45 : 0.18, scale: on ? 1.15 : 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#c92e34]/30 blur-[140px] pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
        {/* Eyebrow */}
        <div className="text-center">
          <motion.div
            initial={{ scale: 0, rotate: 0 }}
            whileInView={{ scale: 1, rotate: -2 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 220, damping: 12 }}
            className="inline-flex items-center gap-2 bg-[#c92e34] text-white px-4 py-1.5 rounded-full border-2 border-white font-anton text-xs tracking-[0.25em] uppercase brutal-shadow-red-sm"
          >
            <Sparkles size={12} /> Flip the script
          </motion.div>

          {/* Morphing headline */}
          <h2 className="font-display text-3xl sm:text-5xl md:text-7xl leading-[0.95] mt-4 md:mt-6 max-w-5xl mx-auto">
            <AnimatePresence mode="wait">
              {on ? (
                <motion.span
                  key="on-line"
                  initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
                  transition={{ duration: 0.4 }}
                  className="block"
                >
                  <span className="text-[#c92e34]">Glow up</span>{" "}
                  <span className="font-handwritten -rotate-2 inline-block text-white">abroad?</span>
                </motion.span>
              ) : (
                <motion.span
                  key="off-line"
                  initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
                  transition={{ duration: 0.4 }}
                  className="block"
                >
                  <span className="text-white">Stuck in</span>{" "}
                  <span className="font-handwritten -rotate-2 inline-block text-[#c92e34]">India?</span>
                </motion.span>
              )}
            </AnimatePresence>
            <span className="block mt-1">Hit the switch.</span>
          </h2>
        </div>

        {/* HUGE CENTRAL TOGGLE */}
        <div className="mt-7 md:mt-16 flex flex-col items-center justify-center">
          <motion.button
            onClick={flip}
            initial={false}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              rotate: [0, -1.5, 1.5, -1, 1, 0],
              y: [0, -3, 0, -2, 0],
            }}
            transition={{
              rotate: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
              y: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
              scale: { type: "spring", stiffness: 320, damping: 18 },
            }}
            className="relative group outline-none focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60 rounded-full cursor-pointer"
            aria-pressed={on}
            aria-label="Toggle: Stuck in India vs Glow up abroad"
            data-testid="problem-solution-switch"
          >
            <div className="absolute inset-0 -m-3 pointer-events-none">
              <Burst trigger={burstTick} />
            </div>

            {/* Always-on glow halo to scream 'click me' */}
            <motion.span
              animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0, 0.55] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
              className="absolute inset-0 rounded-full bg-[#c92e34] blur-md"
              aria-hidden
            />
            <motion.span
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full ring-4 ring-white/40"
              aria-hidden
            />

            {/* Finger-tap hint */}
            <motion.div
              animate={{ y: [0, 4, 0], rotate: [-8, -4, -8] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-7 md:-top-10 left-1/2 -translate-x-1/2 text-2xl md:text-4xl pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
              aria-hidden
            >
              👇
            </motion.div>

            <motion.div
              animate={{
                backgroundColor: on ? "#c92e34" : "#FAFAFA",
                borderColor: on ? "#FAFAFA" : "#282829",
                boxShadow: on ? "10px 10px 0 0 #FAFAFA" : "10px 10px 0 0 #c92e34",
              }}
              transition={{ duration: 0.5, type: "spring", stiffness: 110 }}
              className="relative w-[220px] md:w-[340px] h-[88px] md:h-[136px] rounded-full border-[3px] flex items-center"
            >
              {/* Track labels — always visible inside the track, on the opposite side of the knob */}
              {/* When OFF: knob on left → label on right ("Problem") */}
              <span
                className={`absolute right-3 md:right-10 font-anton text-[11px] md:text-base tracking-[0.18em] md:tracking-[0.22em] uppercase transition-opacity duration-300 ${
                  on ? "opacity-0" : "opacity-100 text-[#282829]"
                }`}
              >
                Problem
              </span>
              {/* When ON: knob on right → label on left ("Solulu") */}
              <span
                className={`absolute left-3 md:left-10 font-anton text-[11px] md:text-base tracking-[0.18em] md:tracking-[0.22em] uppercase transition-opacity duration-300 flex items-center gap-1 ${
                  on ? "opacity-100 text-white" : "opacity-0"
                }`}
              >
                Solulu <Sparkles size={10} className="md:w-3.5 md:h-3.5" />
              </span>

              {/* Knob */}
              <motion.div
                ref={knobRef}
                animate={{
                  x: on ? "calc(100% + 6px)" : "6px",
                  rotate: on ? 360 : 0,
                }}
                transition={{ x: { type: "spring", stiffness: 320, damping: 26 }, rotate: { duration: 0.6 } }}
                className="absolute top-1/2 -translate-y-1/2 w-[78px] md:w-[124px] h-[78px] md:h-[124px] rounded-full grid place-items-center border-[3px] font-anton tracking-widest uppercase text-sm md:text-lg"
                style={{
                  left: 0,
                  background: on ? "#FAFAFA" : "#282829",
                  color: on ? "#c92e34" : "#FAFAFA",
                  borderColor: on ? "#282829" : "#c92e34",
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key={on ? "yes" : "no"}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    {on ? "Yes!" : "No?"}
                  </motion.span>
                </AnimatePresence>
              </motion.div>
            </motion.div>
          </motion.button>

          <motion.p
            key={on ? "hint-on" : "hint-off"}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="hidden md:block mt-3 md:mt-5 text-[10px] md:text-[11px] tracking-[0.25em] uppercase font-anton text-white/55 text-center"
          >
            {on ? "✦ Tap to flip back" : "✦ Tap the switch"}
          </motion.p>
        </div>

        {/* CARD GRID — only the active set is rendered */}
        <div className="mt-8 md:mt-20 max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={on ? "solutions" : "problems"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5"
            >
              {cards.map((item, i) => (
                <Card key={item.title} item={item} side={on ? "right" : "left"} index={i} />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer CTA */}
        <div className="mt-8 md:mt-16 text-center">
          <AnimatePresence mode="wait">
            {on ? (
              <motion.a
                key="cta-on"
                href="#inquiry"
                initial={{ opacity: 0, y: 14, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -14, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 200, damping: 16 }}
                whileHover={{ scale: 1.04, y: -3 }}
                className="inline-flex items-center gap-2 bg-white text-[#282829] hover:bg-[#c92e34] hover:text-white font-bold px-7 py-4 rounded-full border-2 border-white brutal-shadow-red transition-colors"
                data-testid="toggle-cta-on"
              >
                I want this glow-up <ArrowRight size={18} />
              </motion.a>
            ) : (
              <motion.div
                key="cta-off"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-white/60 text-sm md:text-base"
              >
                Sound familiar? <span className="text-white font-bold">Try the switch ↑</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
